import { useQuery } from 'react-query';
import { apiService } from '@/src/services/api-service';
import { HOME_MOVES_ENDPOINTS } from './endpoints';
import { HomeMovesCustomerData } from './types';

type ErrorResponse = {
  error: string;
};

export function useHomeMoveCustomerQuery(accountId?: string | null) {
  return useQuery<HomeMovesCustomerData, ErrorResponse>(
    ['home-moves', 'customer', accountId],
    async () => {
      if (!accountId) {
        throw new Error('Account ID is required');
      }

      const response = await apiService.get<HomeMovesCustomerData>({
        url: HOME_MOVES_ENDPOINTS.retrieveHomeMovesCustomer(accountId),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response;
    },
    {
      enabled: !!accountId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error) => {
        // Don't retry if the customer data doesn't exist (404)
        if (error && 'status' in error && error.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      onError: error => {
        // Only log non-404 errors as 404 is expected when no customer data exists
        if (!error || !('status' in error) || error.status !== 404) {
          console.error('Error fetching home move customer data:', error);
        }
      },
    },
  );
}
